import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth, type UserRole, type OrgTier, type Permission } from '@/contexts/AuthContext'
import {
  LayoutDashboard,
  Image,
  Settings,
  Users,
  Bell,
  Menu,
  X,
  LogOut,
  Wrench,
  CreditCard,
  Upload,
  CheckCircle,
  Clock,
  RotateCcw,
  Shield,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { LucideIcon } from 'lucide-react'
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  getUnreadCount,
  type Notification,
  type NotificationType,
} from '@/lib/notificationsStore'
import { OnboardingTour } from '@/components/OnboardingTour'
import { resetTour } from '@/lib/onboardingStore'

interface NavItem {
  name: string
  href: string
  icon: LucideIcon
  permission?: Permission // If set, only show if user has this permission
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Drops', href: '/drops', icon: Image },
  { name: 'Team', href: '/team', icon: Users, permission: 'manage_team' },
  { name: 'Settings', href: '/settings', icon: Settings, permission: 'view_settings' },
  { name: 'Billing', href: '/settings/billing', icon: CreditCard, permission: 'manage_billing' },
  { name: 'Admin', href: '/admin', icon: Shield, permission: 'super_admin' },
]

const roleLabels: Record<UserRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  viewer: 'Viewer',
  super_admin: 'Super Admin',
}

const tierLabels: Record<OrgTier, string> = {
  free: 'Free',
  pro: 'Pro',
  business: 'Business',
}

export default function DashboardLayout() {
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { organization, hasPermission } = useAuth()

  // Filter navigation based on user permissions
  const visibleNavigation = navigation.filter((item) => {
    // If no permission required, always show
    if (!item.permission) return true
    // Otherwise, check if user has the permission
    return hasPermission(item.permission)
  })

  return (
    <div className="min-h-screen bg-gradient-mesh-subtle">
      {/* Onboarding Tour for new users */}
      <OnboardingTour />

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-card/95 backdrop-blur-md border-r transform transition-transform lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center px-6 border-b">
            <Link to="/dashboard" className="flex items-center space-x-2 group">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center group-hover:scale-110 transition-transform">
                <Image className="h-4 w-4 text-white" />
              </div>
              <span className="text-xl font-bold text-gradient">Photodrop</span>
            </Link>
            <button
              className="ml-auto lg:hidden p-1 hover:bg-muted rounded-lg transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1">
            {visibleNavigation.map((item) => {
              const isActive = location.pathname.startsWith(item.href)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200",
                    isActive
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className={cn(
                    "mr-3 h-5 w-5 transition-transform",
                    isActive && "scale-110"
                  )} />
                  {item.name}
                </Link>
              )
            })}
          </nav>

          {/* Current Role/Tier Info */}
          {organization && (
            <div className="px-4 py-4 border-t bg-muted/30 rounded-t-2xl mx-2 mb-2">
              <div className="text-xs text-muted-foreground mb-1">Current Role</div>
              <div className="text-sm font-semibold">{roleLabels[organization.role]}</div>
              <div className="text-xs text-muted-foreground mt-2 mb-1">Organization Tier</div>
              <div className="text-sm font-semibold flex items-center gap-2">
                {tierLabels[organization.tier]}
                {organization.tier !== 'free' && (
                  <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full">
                    Active
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-background/80 backdrop-blur-md border-b flex items-center px-4 lg:px-8">
          <button
            className="lg:hidden mr-4 p-2 hover:bg-muted rounded-lg transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center space-x-3">
            <DevTools />
            <NotificationCenter />
            <UserButton />
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8 animate-page-enter">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function UserButton() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  return (
    <div className="relative">
      <button
        className="flex items-center space-x-2 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 hover:scale-105 transition-transform"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-secondary text-primary-foreground flex items-center justify-center text-sm font-semibold overflow-hidden shadow-md">
          {user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name || 'User'}
              className="w-full h-full object-cover"
            />
          ) : (
            user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'
          )}
        </div>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-56 rounded-xl shadow-lg bg-card border z-50 overflow-hidden animate-scale-in">
            <div className="py-1">
              <div className="px-4 py-3 border-b bg-muted/30">
                <p className="text-sm font-semibold">{user?.name || 'User'}</p>
                <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
              </div>
              <Link
                to="/settings"
                className="flex items-center px-4 py-2.5 text-sm hover:bg-muted transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <Settings className="mr-3 h-4 w-4" />
                Settings
              </Link>
              <button
                onClick={handleSignOut}
                className="flex items-center w-full px-4 py-2.5 text-sm hover:bg-muted text-destructive transition-colors"
              >
                <LogOut className="mr-3 h-4 w-4" />
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// Dev tools for testing different roles and tiers
function DevTools() {
  const { organization, session, setUserRole, setOrgTier, simulateExpiredSession } = useAuth()
  const [isOpen, setIsOpen] = useState(false)

  // Never ship the role/tier testing panel to production builds. Under `vite build`
  // import.meta.env.DEV is false, matching the gating pattern in lib/env.ts.
  if (!import.meta.env.DEV) return null

  if (!organization) return null

  const roles: UserRole[] = ['owner', 'admin', 'manager', 'viewer', 'super_admin']
  const tiers: OrgTier[] = ['free', 'pro', 'business']

  // Format session info
  const sessionInfo = session
    ? `Expires: ${new Date(session.expiresAt).toLocaleDateString()} (${Math.floor((session.expiresAt - Date.now()) / (1000 * 60 * 60 * 24))} days)`
    : 'No session'

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="gap-2"
      >
        <Wrench className="h-4 w-4" />
        <span className="hidden sm:inline">Dev Tools</span>
      </Button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-72 rounded-md shadow-lg bg-background border z-50 p-4">
            <h3 className="text-sm font-semibold mb-3">Development Tools</h3>

            {/* Role Selector */}
            <div className="mb-4">
              <label className="block text-xs text-muted-foreground mb-2">Test as Role</label>
              <div className="grid grid-cols-2 gap-2">
                {roles.map((role) => (
                  <button
                    key={role}
                    onClick={() => {
                      setUserRole(role)
                      setIsOpen(false)
                    }}
                    className={cn(
                      "px-3 py-1.5 text-xs rounded-md border transition-colors",
                      organization.role === role
                        ? "bg-primary text-primary-foreground border-primary"
                        : "hover:bg-muted"
                    )}
                  >
                    {roleLabels[role]}
                  </button>
                ))}
              </div>
            </div>

            {/* Tier Selector */}
            <div className="mb-4">
              <label className="block text-xs text-muted-foreground mb-2">Test as Tier</label>
              <div className="grid grid-cols-3 gap-2">
                {tiers.map((tier) => (
                  <button
                    key={tier}
                    onClick={() => {
                      setOrgTier(tier)
                      setIsOpen(false)
                    }}
                    className={cn(
                      "px-3 py-1.5 text-xs rounded-md border transition-colors",
                      organization.tier === tier
                        ? "bg-primary text-primary-foreground border-primary"
                        : "hover:bg-muted"
                    )}
                  >
                    {tierLabels[tier]}
                  </button>
                ))}
              </div>
            </div>

            {/* Session Info */}
            <div className="mb-4 pt-3 border-t">
              <label className="block text-xs text-muted-foreground mb-2">Session (30 days)</label>
              <p className="text-xs mb-2">{sessionInfo}</p>
              <button
                onClick={() => {
                  simulateExpiredSession()
                }}
                className="w-full px-3 py-1.5 text-xs rounded-md border border-destructive/20 text-destructive hover:bg-destructive/10 transition-colors"
              >
                Simulate Expired Session
              </button>
            </div>

            {/* Tour Controls */}
            <div className="mb-4 pt-3 border-t">
              <label className="block text-xs text-muted-foreground mb-2">Onboarding Tour</label>
              <button
                onClick={() => {
                  resetTour()
                  setIsOpen(false)
                  window.location.reload()
                }}
                className="w-full px-3 py-1.5 text-xs rounded-md border border-primary/20 text-primary hover:bg-primary/10 transition-colors flex items-center justify-center gap-2"
              >
                <RotateCcw className="h-3 w-3" />
                Restart Onboarding Tour
              </button>
            </div>

            <p className="text-xs text-muted-foreground">
              Role / tier overrides are saved to localStorage for testing.
            </p>
          </div>
        </>
      )}
    </div>
  )
}

// Notification Center Component
function NotificationCenter() {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)

  // Load notifications on mount and periodically
  useEffect(() => {
    const loadNotifications = () => {
      setNotifications(getNotifications())
      setUnreadCount(getUnreadCount())
    }

    loadNotifications()
    // Refresh every 5 seconds
    const interval = setInterval(loadNotifications, 5000)
    return () => clearInterval(interval)
  }, [])

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    markNotificationAsRead(notification.id)
    setNotifications(getNotifications())
    setUnreadCount(getUnreadCount())
    setIsOpen(false)

    // Navigate to relevant drop if applicable
    if (notification.dropId) {
      navigate(`/drops/${notification.dropId}`)
    }
  }

  const handleMarkAllRead = () => {
    markAllNotificationsAsRead()
    setNotifications(getNotifications())
    setUnreadCount(0)
  }

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'upload':
        return <Upload className="h-4 w-4 text-blue-500" />
      case 'drop_closed':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'team_invite':
        return <Users className="h-4 w-4 text-purple-500" />
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />
    }
  }

  const formatTime = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="relative hover:bg-primary/10"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-gradient-to-r from-primary to-destructive text-white text-xs flex items-center justify-center animate-pulse-glow">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-80 rounded-xl shadow-lg bg-card border z-50 overflow-hidden animate-scale-in">
            {/* Header */}
            <div className="px-4 py-3 border-b flex items-center justify-between bg-muted/30">
              <h3 className="font-semibold">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-primary hover:underline font-medium"
                >
                  Mark all as read
                </button>
              )}
            </div>

            {/* Notification list */}
            <div className="max-h-96 overflow-y-auto">
              {notifications.length > 0 ? (
                <div className="divide-y">
                  {notifications.slice(0, 10).map((notification) => (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={cn(
                        "w-full px-4 py-3 text-left hover:bg-muted transition-colors",
                        !notification.read && "bg-primary/5"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 p-1.5 rounded-lg bg-muted">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={cn(
                              "text-sm truncate",
                              !notification.read && "font-semibold"
                            )}>
                              {notification.title}
                            </p>
                            {!notification.read && (
                              <span className="h-2 w-2 bg-primary rounded-full ml-2 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTime(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                    <Bell className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">No notifications yet</p>
                  <p className="text-xs text-muted-foreground mt-1">We'll let you know when something happens</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
